/**
 * useModoTV — comportamiento común de las pantallas de monitoreo en televisor.
 *
 * Un televisor colgado en la pared no tiene a nadie delante: nadie hace scroll,
 * nadie pulsa "actualizar" y nadie vuelve a encender la pantalla si se apaga.
 * Eso obliga a tres cosas que una página normal no necesita:
 *
 *   1. ROTAR las vistas en lugar de desplazarlas. Todo lo que no cabe en una
 *      pantalla se reparte en vistas que se turnan solas.
 *   2. REFRESCAR sin intervención, con un intervalo que el propio equipo pueda
 *      ajustar desde la pantalla.
 *   3. MANTENER la pantalla encendida (Wake Lock) y avisar si se pierde la red,
 *      porque un tablero congelado que parece vivo es peor que uno apagado.
 *
 * SOBRE EL REFRESCO. El portal lee Firestore con `onSnapshot`: las consultas,
 * salas y médicos YA llegan solos en tiempo real, sin coste de sondeo. Poner un
 * temporizador de 5 s encima de eso no traería datos más frescos — solo
 * multiplicaría lecturas. Por eso el intervalo configurable NO relee Firestore:
 * gobierna lo que sí necesita repetirse, que es
 *   · los agregados que vienen de Cloud Functions, y
 *   · el recálculo de todo lo que depende de "ahora" (esperas, antigüedad).
 * De ahí que el mínimo ofrecido sean 10 s y el valor por defecto 30 s: por
 * debajo se gasta cuota sin ganar información.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Opciones de refresco. Ver la nota de arriba sobre por qué no bajan de 10 s. */
export const INTERVALOS_REFRESCO = [
  { ms: 10_000, etiqueta: '10 s' },
  { ms: 30_000, etiqueta: '30 s' },
  { ms: 60_000, etiqueta: '1 min' },
  { ms: 300_000, etiqueta: '5 min' },
] as const;

/** Cuánto permanece cada vista antes de pasar a la siguiente. */
export const INTERVALOS_ROTACION = [
  { ms: 15_000, etiqueta: '15 s' },
  { ms: 30_000, etiqueta: '30 s' },
  { ms: 45_000, etiqueta: '45 s' },
  { ms: 60_000, etiqueta: '1 min' },
  { ms: 90_000, etiqueta: '1 min 30 s' },
  { ms: 120_000, etiqueta: '2 min' },
] as const;

const REFRESCO_POR_DEFECTO = 30_000;
// 1 minuto: el Piso 3D necesita tiempo para recorrer el centro de salud (su recorrido se ajusta a este tiempo).
const ROTACION_POR_DEFECTO = 60_000;

/**
 * Tema del tablero. El valor por defecto es oscuro porque estas pantallas viven
 * encendidas en salas de operación: un fondo claro a todo brillo cansa la vista
 * y se refleja. Pero un televisor junto a una ventana necesita lo contrario, así
 * que la elección queda en cada pantalla. `auto` sigue al sistema.
 */
export type TemaTV = 'oscuro' | 'claro' | 'auto';
const TEMA_POR_DEFECTO: TemaTV = 'oscuro';

interface Opciones {
  /** Identificadores de las vistas entre las que rota la pantalla. */
  vistas: readonly string[];
  /** Clave de almacenamiento: cada televisor recuerda su propia configuración. */
  claveConfig: string;
}

export interface EstadoTV {
  vistaActual: number;
  nombreVista: string;
  irA: (i: number) => void;
  /** Cambia en cada ciclo de refresco: úselo como dependencia para recargar. */
  tick: number;
  /** Momento del último refresco, para mostrarlo en pantalla. */
  ultimoRefresco: Date;
  refrescarAhora: () => void;

  refrescoMs: number;
  setRefrescoMs: (ms: number) => void;
  rotacionMs: number;
  setRotacionMs: (ms: number) => void;

  rotando: boolean;
  setRotando: (v: boolean) => void;
  /** 0→1 dentro de la vista actual, para la barra de progreso. */
  progresoVista: number;

  tema: TemaTV;
  setTema: (t: TemaTV) => void;
  /** Tema ya resuelto (`auto` convertido a claro u oscuro). */
  temaEfectivo: 'oscuro' | 'claro';

  enLinea: boolean;
  pantallaCompleta: boolean;
  alternarPantallaCompleta: () => void;
}

export function useModoTV({ vistas, claveConfig }: Opciones): EstadoTV {
  const guardado = useMemo(() => {
    try {
      const raw = localStorage.getItem(claveConfig);
      return raw
        ? (JSON.parse(raw) as { refrescoMs?: number; rotacionMs?: number; rotando?: boolean; tema?: TemaTV })
        : {};
    } catch {
      return {};
    }
  }, [claveConfig]);

  const [refrescoMs, setRefrescoMs] = useState<number>(guardado.refrescoMs ?? REFRESCO_POR_DEFECTO);
  const [rotacionMs, setRotacionMs] = useState<number>(guardado.rotacionMs ?? ROTACION_POR_DEFECTO);
  const [rotando, setRotando] = useState<boolean>(guardado.rotando ?? true);
  const [tema, setTema] = useState<TemaTV>(guardado.tema ?? TEMA_POR_DEFECTO);

  // Solo importa cuando el tema es `auto`, pero el listener se mantiene siempre:
  // así el cambio de tema del sistema ya está reflejado si alguien elige `auto`.
  const [sistemaOscuro, setSistemaOscuro] = useState<boolean>(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const alCambiar = (e: MediaQueryListEvent) => setSistemaOscuro(e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);

  const temaEfectivo: 'oscuro' | 'claro' =
    tema === 'auto' ? (sistemaOscuro ? 'oscuro' : 'claro') : tema;

  const [vistaActual, setVistaActual] = useState(0);
  const [tick, setTick] = useState(0);
  const [ultimoRefresco, setUltimoRefresco] = useState<Date>(() => new Date());
  const [progresoVista, setProgresoVista] = useState(0);
  // Optimista a propósito: el aviso rojo solo se enciende tras VERIFICAR la
  // falla, nunca por la primera impresión del navegador (ver efecto de red).
  const [enLinea, setEnLinea] = useState(true);
  const [pantallaCompleta, setPantallaCompleta] = useState<boolean>(() => !!document.fullscreenElement);

  useEffect(() => {
    try {
      localStorage.setItem(claveConfig, JSON.stringify({ refrescoMs, rotacionMs, rotando, tema }));
    } catch { /* almacenamiento lleno o bloqueado: no es crítico */ }
  }, [claveConfig, refrescoMs, rotacionMs, rotando, tema]);

  // ── Refresco ───────────────────────────────────────────────────────────────
  const refrescarAhora = useCallback(() => {
    setTick((t) => t + 1);
    setUltimoRefresco(new Date());
  }, []);

  useEffect(() => {
    const id = setInterval(refrescarAhora, refrescoMs);
    return () => clearInterval(id);
  }, [refrescoMs, refrescarAhora]);

  // ── Rotación de vistas ─────────────────────────────────────────────────────
  const inicioVistaRef = useRef<number>(0);

  useEffect(() => {
    inicioVistaRef.current = Date.now();
    setProgresoVista(0);
  }, [vistaActual, rotacionMs, rotando]);

  useEffect(() => {
    if (!rotando || vistas.length <= 1) return;
    // Un solo temporizador corto alimenta la barra de progreso y decide el
    // cambio: dos intervalos separados se desincronizan y la barra "salta".
    const id = setInterval(() => {
      const transcurrido = Date.now() - inicioVistaRef.current;
      if (transcurrido >= rotacionMs) {
        setVistaActual((v) => (v + 1) % vistas.length);
      } else {
        setProgresoVista(transcurrido / rotacionMs);
      }
    }, 250);
    return () => clearInterval(id);
  }, [rotando, rotacionMs, vistas.length]);

  const irA = useCallback((i: number) => {
    setVistaActual(((i % vistas.length) + vistas.length) % vistas.length);
  }, [vistas.length]);

  // ── Estado de red ──────────────────────────────────────────────────────────
  // `navigator.onLine` no comprueba que internet funcione: solo refleja lo que
  // el sistema operativo cree de sus interfaces, y miente con VPNs, adaptadores
  // virtuales y navegadores de televisor (falso "Sin conexión" con los datos
  // llegando en vivo). Por eso los eventos online/offline aquí solo DISPARAN
  // una verificación real contra el propio hosting, y el aviso rojo se enciende
  // únicamente tras DOS fallas seguidas (~5 s): un parpadeo de red no cuenta.
  useEffect(() => {
    let cancelado = false;
    let temporizador: ReturnType<typeof setTimeout> | null = null;
    let fallas = 0;

    const verificar = async () => {
      if (temporizador) { clearTimeout(temporizador); temporizador = null; }
      try {
        await fetch(`${location.origin}/?latido=${Date.now()}`, { method: 'HEAD', cache: 'no-store' });
        if (cancelado) return;
        fallas = 0;
        setEnLinea(true);
      } catch {
        if (cancelado) return;
        fallas += 1;
        if (fallas >= 2) setEnLinea(false);
        // Sigue reintentando: así el aviso también se APAGA solo al volver la
        // red aunque el navegador nunca emita el evento 'online'.
        temporizador = setTimeout(() => { void verificar(); }, 5000);
      }
    };

    const alCambiar = () => { void verificar(); };
    window.addEventListener('online', alCambiar);
    window.addEventListener('offline', alCambiar);
    if (!navigator.onLine) void verificar();

    return () => {
      cancelado = true;
      window.removeEventListener('online', alCambiar);
      window.removeEventListener('offline', alCambiar);
      if (temporizador) clearTimeout(temporizador);
    };
  }, []);

  // ── Pantalla siempre encendida ─────────────────────────────────────────────
  useEffect(() => {
    let sentinel: { release: () => Promise<void> } | null = null;
    let cancelado = false;

    const pedir = async () => {
      try {
        const wl = (navigator as Navigator & {
          wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> };
        }).wakeLock;
        if (!wl) return;
        const s = await wl.request('screen');
        if (cancelado) { void s.release(); return; }
        sentinel = s;
      } catch { /* el navegador puede negarlo sin interacción previa */ }
    };
    void pedir();

    // El bloqueo se pierde al minimizar o cambiar de pestaña: se vuelve a pedir.
    const alVolver = () => { if (document.visibilityState === 'visible') void pedir(); };
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      cancelado = true;
      document.removeEventListener('visibilitychange', alVolver);
      void sentinel?.release().catch(() => undefined);
    };
  }, []);

  // ── Pantalla completa ──────────────────────────────────────────────────────
  useEffect(() => {
    const alCambiar = () => setPantallaCompleta(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', alCambiar);
    return () => document.removeEventListener('fullscreenchange', alCambiar);
  }, []);

  const alternarPantallaCompleta = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void document.documentElement.requestFullscreen().catch(() => undefined);
    }
  }, []);

  // ── Teclado: un mando de TV o un teclado inalámbrico bastan para operarlo ──
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      // La pantalla de TV no tiene campos de escritura hoy, pero los atajos de
      // una letra ya costaron tres issues en la consulta (#36, #44, #45): si
      // alguna vez se agrega un campo aquí, que no se repita.
      const d = e.target as HTMLElement | null;
      if (d instanceof HTMLInputElement || d instanceof HTMLTextAreaElement || d?.isContentEditable) return;
      if (e.key === 'ArrowRight') irA(vistaActual + 1);
      else if (e.key === 'ArrowLeft') irA(vistaActual - 1);
      else if (e.key === ' ') { e.preventDefault(); setRotando((r) => !r); }
      else if (e.key.toLowerCase() === 'f') alternarPantallaCompleta();
      else if (e.key.toLowerCase() === 'r') refrescarAhora();
      else if (e.key.toLowerCase() === 't') setTema((t) => (t === 'oscuro' ? 'claro' : 'oscuro'));
    };
    window.addEventListener('keydown', alPulsar);
    return () => window.removeEventListener('keydown', alPulsar);
  }, [irA, vistaActual, alternarPantallaCompleta, refrescarAhora]);

  return {
    vistaActual,
    nombreVista: vistas[vistaActual] ?? '',
    irA,
    tick,
    ultimoRefresco,
    refrescarAhora,
    refrescoMs,
    setRefrescoMs,
    rotacionMs,
    setRotacionMs,
    rotando,
    setRotando,
    progresoVista,
    tema,
    setTema,
    temaEfectivo,
    enLinea,
    pantallaCompleta,
    alternarPantallaCompleta,
  };
}
